import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../components/Logo";
import ProgressSteps from "../../../components/ProgressSteps";
import { signup } from "../../../api/authEtablissement";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const SIGNUP_STORAGE_KEY = "maps.signup";
const SIGNUP_CREDENTIALS_KEY = "agbaza.signup.credentials";

type StoredSignup = {
  mode?: string;
  etablissementId?: string;
  etablissementNom?: string;
  etablissementSigle?: string | null;
  etablissementVille?: string | null;
  etablissementType?: string;
};

export default function EtablissementSignupCredentials() {
  const navigate = useNavigate();
  const [base, setBase] = useState<StoredSignup | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [claimError, setClaimError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(SIGNUP_STORAGE_KEY);
    if (!raw) {
      navigate("/etablissement/inscription/recherche", { replace: true });
      return;
    }
    const parsed = JSON.parse(raw) as StoredSignup & { email?: string };
    setBase(parsed);
    if (typeof parsed.email === "string") setEmail(parsed.email);
    try {
      const credRaw = sessionStorage.getItem(SIGNUP_CREDENTIALS_KEY);
      if (credRaw) {
        const cred = JSON.parse(credRaw) as { password?: string; confirmPassword?: string };
        if (typeof cred.password === "string") setPassword(cred.password);
        if (typeof cred.confirmPassword === "string") setConfirm(cred.confirmPassword);
      }
    } catch {
      /* ignore */
    }
  }, [navigate]);

  const isClaim = base?.mode === "claim";

  const emailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const strong = useMemo(() => passwordRegex.test(password), [password]);
  const match = confirm === password && confirm.length > 0;
  const canProceed = emailValid && strong && match && !submitting;

  const restartSearch = () => {
    localStorage.removeItem(SIGNUP_STORAGE_KEY);
    navigate("/etablissement/inscription/recherche");
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canProceed || !base) return;
    setError("");
    setClaimError(null);

    if (isClaim) {
      if (!base.etablissementId) return;
      setSubmitting(true);
      const result = await signup({
        email: email.trim(),
        password,
        confirmPassword: confirm,
        type_utilisateur: base.etablissementType ?? "",
        profileData: { etablissementId: base.etablissementId },
      });
      setSubmitting(false);
      if (result.ok) {
        localStorage.removeItem(SIGNUP_STORAGE_KEY);
        navigate("/etablissement/inscription/succes", { state: { mode: "claim" } });
      } else if (result.kind === "not_found" || result.kind === "already_claimed") {
        setClaimError({ message: result.message, retryable: true });
      } else {
        setError(result.message || "Une erreur est survenue lors de l'inscription.");
      }
      return;
    }

    try {
      localStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify({ ...base, email: email.trim() }));
      sessionStorage.setItem(
        SIGNUP_CREDENTIALS_KEY,
        JSON.stringify({ password, confirmPassword: confirm })
      );
    } catch {
      /* ignore */
    }
    navigate("/etablissement/inscription/documents");
  };

  if (!base) return null;

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 relative">
      {/* Mobile background image */}
      <div className="absolute inset-0 md:hidden">
        <img src={'/maps-login-side.jpg'} alt="signup-credentials" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Left: form */}
      <div className="relative flex items-center justify-center px-4 md:px-10 py-10">
        <div className="w-full max-w-md">
          <div className="flex justify-center"><Logo size={56} /></div>
          <div className="mt-6 text-sm" style={{ color: "#00553E" }}>
            <Link
              to={isClaim ? "/etablissement/inscription/recherche" : "/etablissement/inscription/informations"}
              className="inline-flex items-center gap-2"
            >
              <ArrowLeft size={16} /> Retour
            </Link>
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-[#0B1F16]">
            Créer le compte de l'établissement
          </h1>
          <div className="mt-1 text-gray-500 font-semibold">Identifiants et sécurité</div>

          {isClaim && (
            <div className="mt-4 text-sm bg-[#F3FAF7] border rounded-[10px] px-3 py-2" style={{ borderColor: "#DCEFE9" }}>
              Établissement sélectionné : <span className="font-semibold">{base.etablissementNom}</span>
              {base.etablissementVille ? ` — ${base.etablissementVille}` : ""}
            </div>
          )}

          {claimError && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2 space-y-2">
              <div>{claimError.message}</div>
              {claimError.retryable && (
                <button type="button" onClick={restartSearch} className="font-semibold underline cursor-pointer">
                  Relancer la recherche
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
              {error}
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <label className="text-sm">Email</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value.replace(/\s/g, ""))}
                placeholder="exemple@domaine.com"
                className={`w-full h-12 rounded-[10px] border px-3 bg-white ${emailValid || email === '' ? 'border-[#E5E7EB]' : 'border-red-400'}`}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={`w-full h-12 rounded-[10px] border px-3 bg-white ${strong || password === '' ? 'border-[#E5E7EB]' : 'border-red-400'}`}
              />
              <div className="text-xs text-gray-600">Au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial.</div>
            </div>
            <div className="space-y-2">
              <label className="text-sm">Confirmer mot de passe</label>
              <input
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                className={`w-full h-12 rounded-[10px] border px-3 bg-white ${match || confirm === '' ? 'border-[#E5E7EB]' : 'border-red-400'}`}
              />
            </div>

            <button
              type="submit"
              className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
              style={{ backgroundColor: canProceed ? "#00553E" : "#DCEFE9", color: canProceed ? "#FFFFFF" : "#7aa79a" }}
              disabled={!canProceed}
            >
              {isClaim ? (submitting ? "Création du compte…" : "Créer le compte") : "Suivant"}
            </button>
          </form>

          <div className="mt-8">
            <ProgressSteps step={isClaim ? 2 : 3} total={isClaim ? 3 : 5} />
          </div>
        </div>
      </div>

      {/* Right: image (desktop) */}
      <div className="relative hidden md:block">
        <img src={'/maps-login-side.jpg'} alt="signup-credentials" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
